import {
  InvalidInput,
  removeTrack,
  setTrack,
  type Plan,
  type PlanStop,
  type Track,
} from '@trippath/shared';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { minutesSaid } from '../../lib/clock';
import { kmApart } from '../../lib/map';

/** The files a track is read from. */
const ACCEPT = '.gpx,.kml,.kmz,.geojson,.json,.csv,.txt,.fit,.tcx';

/** The profile's box: as wide as the widget, a hand high. */
const WIDE = 300;
const HIGH = 64;

/**
 * The heights of a track along its length, as a line: each kept point at how far along it is,
 * its height scaled between the lowest and the highest. A point without a height breaks it.
 */
export function profileOf(
  track: Pick<Track, 'path' | 'heights'>,
  wide = WIDE,
  high = HIGH,
): string {
  const { path, heights } = track;
  // Each point: how far along it is, and its height.
  let along = 0;
  const steps = path.map((point, index) => {
    const before = path[index - 1];
    if (before) {
      along += kmApart({ lon: before[0], lat: before[1] }, { lon: point[0], lat: point[1] });
    }
    return { km: along, height: heights[index] };
  });
  const said = heights.filter((height) => height !== null);
  const low = Math.min(...said);
  const span = Math.max(1, Math.max(...said) - low);
  const x = (km: number) => ((km / Math.max(along, 0.001)) * wide).toFixed(1);
  const y = (height: number) => (high - 4 - ((height - low) / span) * (high - 8)).toFixed(1);
  let open = false;
  return steps
    .map(({ km, height }) => {
      if (typeof height !== 'number') {
        open = false;
        return '';
      }
      const move = open ? 'L' : 'M';
      open = true;
      return `${move}${x(km)} ${y(height)}`;
    })
    .filter((step) => step !== '')
    .join(' ');
}

/**
 * A way's track, from a file: what it adds up to — how far, how much up and down, how high, how
 * long — its heights along it, and with `onPlan`, a file to put in its place or take it off;
 * without one, a file to import as it.
 */
export function WayTrack({
  plan,
  stop,
  onPlan,
}: {
  plan: Plan;
  stop: PlanStop;
  /** Keeps the plan a track changed; without it the track is only said. */
  onPlan?: ((plan: Plan) => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ids = useId();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<'file' | 'save' | null>(null);
  const track = stop.track;
  if (!track && !onPlan) return null;

  const done = (work: Promise<Plan>) => {
    setBusy(true);
    setFailed(null);
    work
      .then(
        (next) => {
          onPlan?.(next);
        },
        (error: unknown) => {
          setFailed(error instanceof InvalidInput ? 'file' : 'save');
        },
      )
      .finally(() => {
        setBusy(false);
      });
  };
  const chooser = (label: string) => (
    <label className="button button-small way-track-pick">
      {label}
      <input
        type="file"
        accept={ACCEPT}
        className="visually-hidden"
        aria-describedby={`${ids}-hint`}
        onChange={(event) => {
          const file = event.target.files?.item(0);
          event.target.value = '';
          if (file) done(setTrack(plan.trip.id, stop.id, file));
        }}
      />
    </label>
  );
  const km = (metres: number) =>
    t('journeys.km', {
      km: (metres / 1000).toLocaleString(lang, {
        maximumFractionDigits: metres < 10_000 ? 1 : 0,
      }),
    });
  const profile = track ? profileOf(track) : '';

  return (
    <section className="way-track" aria-labelledby={`${ids}-title`}>
      <h3 id={`${ids}-title`} className="way-track-title">
        {t('track.title')}
      </h3>
      {track && (
        <>
          <p className="quiet">
            {t('track.from', { name: track.name, format: track.format.toUpperCase() })}
          </p>
          <dl className="way-facts">
            <div>
              <dt>{t('track.length')}</dt>
              <dd>{km(track.metres)}</dd>
            </div>
            {track.ascent !== null && track.descent !== null && (
              <div>
                <dt>{t('track.climb')}</dt>
                <dd>
                  ↑ {track.ascent} m · ↓ {track.descent} m
                </dd>
              </div>
            )}
            {track.lowest !== null && track.highest !== null && (
              <div>
                <dt>{t('track.height')}</dt>
                <dd>
                  {track.lowest}–{track.highest} m
                </dd>
              </div>
            )}
            {track.seconds !== null && (
              <div>
                <dt>{t('track.time')}</dt>
                <dd>{minutesSaid(Math.round(track.seconds / 60))}</dd>
              </div>
            )}
          </dl>
          {profile !== '' && track.lowest !== null && track.highest !== null && (
            <svg
              className="way-track-profile"
              viewBox={`0 0 ${String(WIDE)} ${String(HIGH)}`}
              preserveAspectRatio="none"
              role="img"
              aria-label={t('track.profile', { low: track.lowest, high: track.highest })}
            >
              <path d={profile} />
            </svg>
          )}
        </>
      )}
      {onPlan && (
        <div className="way-track-actions">
          {chooser(t(track ? 'track.replace' : 'track.import'))}
          <Link
            className="button button-small"
            to={`/trips/${plan.trip.id}/stops/${stop.id}/way`}
          >
            {t('wayDraw.link')}
          </Link>
          {/* Along a road, what is worth a stop on the way. */}
          {stop.out && stop.out.mode !== 'flight' && stop.out.mode !== 'ferry' && (
            <Link
              className="button button-small"
              to={`/trips/${plan.trip.id}/stops/${stop.id}/sights`}
            >
              {t('sights.link')}
            </Link>
          )}
          {track && (
            <button
              type="button"
              className="button button-small button-quiet"
              disabled={busy}
              onClick={() => {
                done(removeTrack(plan.trip.id, stop.id));
              }}
            >
              {t('track.remove')}
            </button>
          )}
        </div>
      )}
      {onPlan && (
        <p className="quiet way-track-hint" id={`${ids}-hint`}>
          {busy ? t('track.reading') : t('track.importHint')}
        </p>
      )}
      {failed && (
        <p className="form-failed" role="alert">
          {t(failed === 'file' ? 'track.failed' : 'track.saveFailed')}
        </p>
      )}
    </section>
  );
}
